/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useEffect } from 'react';
import type { ShopperProducts } from '@/scapi';
import {
    assignStJudeGlobal,
    buildCategoryBreadcrumbSegment,
    buildStJudePagePath,
    buildStJudeProductEntry,
    wrapStJudeGlobal,
} from '@/lib/tracking/stjude-datalayer';
import { useStJudeRootLoaderData } from './use-stjude-root-loader-data';

export interface StJudeProductDataLayerSyncProps {
    productId: string;
    productName: string;
    /** The product's (already-resolved) primary category, or `undefined` when it has none (e.g. some variant products). */
    category: ShopperProducts.schemas['Category'] | undefined;
}

/**
 * Corrects `window.stjude.digitalData.page` with
 * `/s/{siteId}/{categoryBreadcrumb}/{productID}` once the product's primary
 * category is known — the root loader (and `StJudeDataLayerSync`, which
 * explicitly skips this route — see its doc comment) only knows the bare
 * product ID from the URL. Falls back to a bare `/s/{siteId}/{productID}`
 * segment when there's no category. Also sets `digitalData.product` to the
 * single-entry `productInfo` array legacy's PDP branch populates (see
 * `StJudeProductEntry`'s doc comment in stjude-datalayer.ts).
 *
 * The product route's `loaderData.category` is a *deferred* promise — mount
 * this once it resolves (e.g. inside the same `<Await resolve={loaderData.category}>`
 * already used to render `CategoryBreadcrumbs`), passing the resolved value
 * (or `undefined` on no-category/error). Non-visual (renders nothing).
 */
export function StJudeProductDataLayerSync({ productId, productName, category }: StJudeProductDataLayerSyncProps) {
    const rootData = useStJudeRootLoaderData();

    useEffect(() => {
        if (!rootData?.omnitureConfig?.omnitureEnabled || !rootData.stJudeDigitalData || !rootData.stJudeSiteId) {
            return;
        }

        const segment = category ? `${buildCategoryBreadcrumbSegment(category)}/${productId}` : productId;

        assignStJudeGlobal(
            wrapStJudeGlobal({
                ...rootData.stJudeDigitalData,
                page: {
                    ...rootData.stJudeDigitalData.page,
                    pageName: buildStJudePagePath(rootData.stJudeSiteId, segment),
                    type: 'prodDetail',
                },
                product: [buildStJudeProductEntry(productId, productName)],
            })
        );
    }, [productId, productName, category, rootData]);

    return null;
}
